import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  RefreshCw,
  Download,
  Search,
  Trash2,
  CalendarDays,
  Clock,
  MapPin,
  Phone,
  Car,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authed = axios.create({ withCredentials: true });
authed.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401 && !err.config._retried) {
      err.config._retried = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return authed(err.config);
      } catch {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

const STATUS = {
  en_attente: { label: "En attente", cls: "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#E6CA65]" },
  confirme: { label: "Confirmé", cls: "border-green-500/40 bg-green-500/10 text-green-400" },
  termine: { label: "Terminé", cls: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400" },
  annule: { label: "Annulé", cls: "border-red-500/40 bg-red-500/10 text-red-400" },
};

const TABS = [
  { id: "tous", label: "Tous" },
  { id: "en_attente", label: "En attente" },
  { id: "confirme", label: "Confirmés" },
  { id: "termine", label: "Terminés" },
  { id: "annule", label: "Annulés" },
];

const formatDate = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("tous");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [b, s] = await Promise.all([authed.get(`${API}/bookings`), authed.get(`${API}/bookings/stats`)]);
      setBookings(b.data);
      setStats(s.data);
    } catch {
      toast.error("Impossible de charger les rendez-vous.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await authed.patch(`${API}/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
      load(true);
      toast.success(`Statut mis à jour : ${STATUS[status].label}`);
    } catch {
      toast.error("Échec de la mise à jour du statut.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await authed.delete(`${API}/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      load(true);
      toast.success("Demande supprimée.");
    } catch {
      toast.error("Échec de la suppression.");
    }
  };

  const exportCSV = () => {
    const header = ["Date", "Créneau", "Nom", "Téléphone", "Véhicule", "Service", "Lieu", "Adresse", "Statut", "Message"];
    const rows = filtered.map((b) => [
      b.date,
      b.time_slot,
      b.name,
      b.phone,
      b.vehicle,
      b.service,
      b.intervention_type === "domicile" ? "Domicile" : "Atelier",
      b.address || "",
      STATUS[b.status]?.label || b.status,
      (b.message || "").replace(/\n/g, " "),
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `motor-select-rdv-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = bookings.filter(
    (b) =>
      (tab === "tous" || b.status === tab) &&
      (search === "" || `${b.name} ${b.phone} ${b.vehicle}`.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-40 border-b border-[#D4AF37]/20 bg-black/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-auto" />
            <div>
              <p className="font-display text-xl tracking-[0.15em] text-white">
                MOTOR <span className="text-[#D4AF37]">SELECT</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Espace pro</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-zinc-400 sm:block" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10" data-testid="admin-dashboard">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="font-display text-4xl uppercase text-white sm:text-5xl">
            Rendez-<span className="text-gold-gradient">vous</span>
          </h1>
          <p className="mt-2 text-sm text-zinc-500">Demandes de prise de rendez-vous en ligne</p>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { label: "Total", value: stats?.total ?? "—", testid: "stat-total" },
            { label: "En attente", value: stats?.en_attente ?? "—", gold: true, testid: "stat-en-attente" },
            { label: "Confirmés", value: stats?.confirme ?? "—", testid: "stat-confirmes" },
            { label: "Aujourd'hui", value: stats?.aujourdhui ?? "—", testid: "stat-aujourdhui" },
          ].map((s) => (
            <div
              key={s.label}
              data-testid={s.testid}
              className={`rounded-2xl border p-6 ${s.gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0D0D0F]"}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
              <p className={`mt-3 font-display text-4xl ${s.gold ? "text-gold-gradient" : "text-white"}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-all ${
                  tab === t.id
                    ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                    : "border-white/15 bg-white/5 text-zinc-400 hover:border-[#D4AF37]/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="admin-search-input"
                placeholder="Client, téléphone, véhicule..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64 rounded-full border border-white/10 bg-white/5 py-2 pl-10 pr-4 text-sm text-white placeholder-zinc-500 outline-none transition-colors focus:border-[#D4AF37]"
              />
            </div>
            <button
              onClick={() => load()}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#D4AF37]/50 hover:text-[#E6CA65]"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Actualiser
            </button>
            <button
              onClick={exportCSV}
              data-testid="admin-export-button"
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#D4AF37]/50 hover:text-[#E6CA65]"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0D0D0F]" data-testid="admin-bookings-table">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center" data-testid="admin-empty-state">
              <p className="font-display text-2xl uppercase text-zinc-600">Aucune demande</p>
              <p className="mt-2 text-sm text-zinc-600">Les nouvelles réservations apparaîtront ici.</p>
            </div>
          ) : (
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-zinc-500">
                  <th className="px-5 py-4">Rendez-vous</th>
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Véhicule</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Lieu</th>
                  <th className="px-5 py-4">Statut</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`admin-booking-row-${b.id.slice(0, 8)}`}
                    className="border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                  >
                    <td className="px-5 py-4">
                      <p className="flex items-center gap-2 font-semibold text-white">
                        <CalendarDays className="h-4 w-4 text-[#D4AF37]" /> {formatDate(b.date)}
                      </p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                        <Clock className="h-3 w-3" /> {b.time_slot}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-white">{b.name}</p>
                      <a
                        href={`tel:${b.phone.replace(/\s/g, "")}`}
                        className="mt-1 flex items-center gap-1 text-xs text-[#E6CA65] hover:underline"
                      >
                        <Phone className="h-3 w-3" /> {b.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4">
                      <p className="flex items-center gap-2 text-zinc-300">
                        <Car className="h-4 w-4 text-zinc-500" /> {b.vehicle}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-zinc-300">{b.service}</td>
                    <td className="px-5 py-4">
                      <p className="flex items-center gap-2 text-zinc-300">
                        <MapPin className="h-4 w-4 text-zinc-500" />
                        {b.intervention_type === "domicile" ? "Domicile" : "Atelier"}
                      </p>
                      {b.intervention_type === "domicile" && b.address && (
                        <p className="mt-1 max-w-[180px] truncate text-xs text-zinc-500">{b.address}</p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-status-select-${b.id.slice(0, 8)}`}
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${STATUS[b.status]?.cls || ""} bg-transparent`}
                      >
                        {Object.entries(STATUS).map(([id, s]) => (
                          <option key={id} value={id} className="bg-[#0D0D0F] text-white">
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        data-testid={`admin-delete-button-${b.id.slice(0, 8)}`}
                        onClick={() => remove(b.id)}
                        aria-label="Supprimer"
                        className="rounded-lg p-2 text-zinc-600 transition-colors hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
